import React, { useState, useEffect, useCallback } from "react";
import { v4 as uuidv4 } from "uuid"; // Import the UUID library
import ProductAutocomplete from "../common/ProductAutocomplete";
import "../common/Modal.css";
import "../../assets/styles/common.css";
import Notification from "../common/Notification";

const TransactionModal = ({
  isOpen,
  onClose,
  onSubmit,
  onVoid,
  products,
  transaction,
  mode,
}) => {
  const [transactionDate, setTransactionDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [type, setType] = useState("sale");
  const [notes, setNotes] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [notification, setNotification] = useState(null);
  const [allAvailableProducts, setAllAvailableProducts] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNotification(null);
      setAllAvailableProducts(products || []);
      if (mode === "form") {
        setTransactionDate(new Date().toISOString().split("T")[0]);
        setType("sale");
        setNotes("");
        setSelectedItems([]);
        setShowConfirmation(false);
      }
    }
  }, [isOpen, products, mode, transaction]);

  const getStockColorClass = (stocks) => {
    if (stocks <= 10) return "low-stock-red";
    if (stocks <= 25) return "low-stock-orange";
    if (stocks <= 50) return "low-stock-yellow";
    return "";
  };

  const handleAddProductToTransaction = (product) => {
    setSelectedItems((prevSelected) => {
      // Use the actual product ID for existence check
      if (prevSelected.find((item) => item.productId === product.id)) {
        return prevSelected;
      }
      return [
        ...prevSelected,
        {
          id: uuidv4(), // Use a unique UUID for the frontend key
          productId: product.id, // Store the actual database product ID
          brandName: product.brandName,
          genericName: product.genericName,
          totalStocks: product.totalStocks,
          quantity: 1,
          unitPrice: parseFloat(product.price || 0).toFixed(2),
          discount: "0.00",
        },
      ];
    });
  };

  // --- START OF FIX: Directly use 'type' from state ---
  const handleUpdateItemQuantity = (itemId, newQuantity) => {
    setSelectedItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === itemId) {
          const parsedQuantity = parseInt(newQuantity) || 1;
          const minQuantity = 1;
          let finalQuantity = parsedQuantity;

          if (type === "sale") {
            // For a SALE, limit the quantity by available stock
            const maxQuantity = item.totalStocks;
            finalQuantity = Math.max(
              minQuantity,
              Math.min(parsedQuantity, maxQuantity)
            );
          } else if (type === "refund") {
            // For a REFUND (or other types), only ensure it's at least 1
            finalQuantity = Math.max(minQuantity, parsedQuantity);
          }

          return {
            ...item,
            quantity: finalQuantity,
          };
        }
        return item;
      })
    );
  };
  // --- END OF FIX ---

  const handleUpdateItemUnitPrice = (itemId, newPrice) => {
    setSelectedItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId
          ? { ...item, unitPrice: parseFloat(newPrice || 0).toFixed(2) }
          : item
      )
    );
  };

  const handleUpdateItemDiscount = (itemId, newDiscount) => {
    setSelectedItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, discount: newDiscount } : item
      )
    );
  };

  const handleRemoveItem = (itemId) => {
    setSelectedItems((prevItems) =>
      prevItems.filter((item) => item.id !== itemId)
    );
  };

  const getParsedDiscount = (item) => {
    let discountAmount = 0;
    const itemPrice = parseFloat(item.unitPrice || 0);
    const itemQuantity = parseInt(item.quantity || 0);
    const discountValue = item.discount.trim();

    if (discountValue.includes("%")) {
      const percentage = parseFloat(discountValue.replace("%", ""));
      if (!isNaN(percentage)) {
        discountAmount = itemPrice * itemQuantity * (percentage / 100);
      }
    } else {
      discountAmount = parseFloat(discountValue);
      if (isNaN(discountAmount)) {
        discountAmount = 0;
      }
    }
    return {
      amount: discountAmount,
      type: discountValue.includes("%") ? "percentage" : "amount",
      displayValue: discountValue,
    };
  };

  const calculateTotalAmount = useCallback(() => {
    return selectedItems
      .reduce((total, item) => {
        const itemPrice = parseFloat(item.unitPrice || 0);
        const itemQuantity = parseInt(item.quantity || 0);
        const { amount: discountAmount } = getParsedDiscount(item);
        const lineTotal = itemPrice * itemQuantity - discountAmount;
        return total + lineTotal;
      }, 0)
      .toFixed(2);
  }, [selectedItems]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setNotification(null);

    if (!transactionDate || selectedItems.length === 0) {
      setNotification({
        message:
          "Please fill in all required fields and add at least one product.",
        type: "error",
      });
      return;
    }
    setShowConfirmation(true);
  };

  const handleFinalSubmit = async () => {
    setNotification(null);
    const transactionItemsData = selectedItems.map((item) => {
      const itemPrice = parseFloat(item.unitPrice);
      const itemQuantity = parseInt(item.quantity);
      const { amount: discountAmount } = getParsedDiscount(item);
      const lineTotal = itemPrice * itemQuantity - discountAmount;

      return {
        productId: item.productId, // Use the actual database product ID here
        quantity: itemQuantity,
        unitPrice: itemPrice,
        discount: discountAmount.toFixed(2),
        lineTotal: lineTotal.toFixed(2),
      };
    });

    const transactionData = {
      transactionDate,
      type,
      notes: notes.trim(),
      totalAmount: parseFloat(calculateTotalAmount()),
      transactionItems: transactionItemsData,
    };

    try {
      await onSubmit(null, transactionData);
      setShowConfirmation(false);
    } catch (error) {
      setNotification({
        message: error.message || `Failed to record transaction.`,
        type: "error",
      });
    }
  };

  // NEW FUNCTION: Handle printing the view content
  const handlePrint = () => {
    // This will trigger the browser's print dialog.
    // We'll use CSS to style only the modal content for printing.
    window.print();
  };
  // END NEW FUNCTION

  if (!isOpen) return null;

  const renderConfirmationContent = () => (
    <div className="modal-overlay">
      <div className="modal-content medium-modal">
        <div className="modal-header">
          <h2>Confirm Transaction Details</h2>
          <button className="modal-close-button" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="modal-body">
          <p>
            <strong>Transaction Date:</strong> {transactionDate}
          </p>
          <p>
            <strong>Transaction Type:</strong> {type}
          </p>
          <p>
            <strong>Notes:</strong> {notes || "N/A"}
          </p>
          <h4 style={{ marginTop: "20px" }}>Items to be Added:</h4>
          <table className="user-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Quantity</th>
                <th>Unit Price (₱)</th>
                <th>Discount</th>
                <th>Line Total (₱)</th>
              </tr>
            </thead>
            <tbody>
              {selectedItems.map((item) => {
                const { amount: discountAmount, type: discountType } =
                  getParsedDiscount(item);
                return (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.brandName}</strong> ({item.genericName})
                    </td>
                    <td>{item.quantity}</td>
                    <td>₱{item.unitPrice}</td>
                    <td>
                      {discountType === "percentage"
                        ? `${item.discount}`
                        : `₱${discountAmount.toFixed(2)}`}
                    </td>
                    <td>
                      ₱
                      {(
                        parseFloat(item.unitPrice) * parseInt(item.quantity) -
                        discountAmount
                      ).toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td
                  colSpan="4"
                  style={{ textAlign: "right", fontWeight: "bold" }}
                >
                  Total Amount:
                </td>
                <td style={{ fontWeight: "bold" }}>
                  ₱{calculateTotalAmount()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="button button-primary"
            onClick={handleFinalSubmit}
          >
            Confirm & Submit
          </button>
          <button
            type="button"
            className="button button-secondary"
            onClick={() => setShowConfirmation(false)}
          >
            Back to Form
          </button>
        </div>
      </div>
    </div>
  );

  const renderFormContent = () => (
    <div className="modal-content large-modal">
      <div className="modal-header">
        <h2>Add New Transaction</h2>
        <button className="modal-close-button" onClick={onClose}>
          &times;
        </button>
      </div>
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <form onSubmit={handleSubmit} className="modal-form">
        <div className="form-columns">
          <div className="form-group">
            <label htmlFor="transactionDate">Transaction Date:</label>
            <input
              type="date"
              id="transactionDate"
              value={transactionDate}
              onChange={(e) => setTransactionDate(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="type">Transaction Type:</label>
            <select
              id="type"
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                // Clear items to prevent confusion with old quantity limits
                setSelectedItems([]);
              }}
              required
            >
              <option value="sale">Sale</option>
              <option value="refund">Refund</option>
            </select>
          </div>
        </div>

        <label htmlFor="productSearch">Add Products:</label>
        <ProductAutocomplete
          products={allAvailableProducts}
          onSelect={handleAddProductToTransaction}
          selectedIds={selectedItems.map((item) => item.productId)} // Use productId for comparison
          placeholder="Search products by brand, generic name, or distributor..."
          getStockColorClass={getStockColorClass}
        />

        {selectedItems.length > 0 && (
          <div className="transaction-summary">
            <h4>Selected Products ({selectedItems.length})</h4>
            <table className="user-table">
              <thead>
                <tr>
                  <th>Product</th>
                  {/* Conditionally show Available Stock for Sale only */}
                  {type === "sale" && <th>Available Stock</th>}
                  <th>Quantity</th>
                  <th>Unit Price (₱)</th>
                  <th>Discount (₱ or %)</th>
                  <th>Line Total (₱)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedItems.map((item) => {
                  const { amount: discountAmount } = getParsedDiscount(item);
                  return (
                    <tr key={item.id}>
                      <td>
                        <strong>{item.brandName}</strong> ({item.genericName})
                      </td>
                      {type === "sale" && <td>{item.totalStocks}</td>}
                      <td>
                        <input
                          type="number"
                          min="1"
                          // For Sale, set max to stock; for Refund, use a high number or omit
                          max={type === "sale" ? item.totalStocks : 99999}
                          value={item.quantity}
                          onChange={(e) =>
                            handleUpdateItemQuantity(item.id, e.target.value)
                          }
                          className="form-control"
                          style={{ width: "80px" }}
                          disabled={item.totalStocks === 0 && type === "sale"}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unitPrice}
                          onChange={(e) =>
                            handleUpdateItemUnitPrice(item.id, e.target.value)
                          }
                          className="form-control"
                          style={{ width: "100px" }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={item.discount}
                          onChange={(e) =>
                            handleUpdateItemDiscount(item.id, e.target.value)
                          }
                          className="form-control"
                          style={{ width: "80px" }}
                        />
                      </td>
                      <td>
                        {(
                          parseFloat(item.unitPrice || 0) *
                            parseInt(item.quantity || 0) -
                          discountAmount
                        ).toFixed(2)}
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(item.id)}
                          className="button button-danger button-small"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td
                    // Adjust colspan based on whether "Available Stock" column is visible
                    colSpan={type === "sale" ? 6 : 5}
                    style={{ textAlign: "right", fontWeight: "bold" }}
                  >
                    Total Amount:
                  </td>
                  <td colSpan="1" style={{ fontWeight: "bold" }}>
                    ₱{calculateTotalAmount()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="notes">Notes (Optional):</label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="form-control"
            rows="3"
          ></textarea>
        </div>

        <div className="modal-actions">
          <button type="submit" className="button button-primary">
            Review Transaction
          </button>
          <button
            type="button"
            className="button button-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );

  const renderViewContent = () => (
    <div className="modal-content medium-modal print-area">
      {" "}
      {/* Added print-area class */}
      <div className="modal-header">
        <h2>Transaction Details</h2>
        <button className="modal-close-button" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="modal-body">
        <p>
          <strong>ID:</strong> #{transaction.id}
        </p>
        <p>
          <strong>User:</strong> {transaction.user?.username || "N/A"}
        </p>
        <p>
          <strong>Type:</strong> {transaction.type}
        </p>
        <p>
          <strong>Total Amount:</strong> ₱
          {parseFloat(transaction.totalAmount).toFixed(2)}
        </p>
        <p>
          <strong>Date:</strong>{" "}
          {new Date(transaction.createdAt).toLocaleString()}
        </p>
        <p>
          <strong>Status:</strong>{" "}
          <span
            className={
              transaction.status === "voided"
                ? "status-voided"
                : "status-active"
            }
          >
            {transaction.status === "voided" ? "Voided" : "Active"}
          </span>
        </p>
        <p>
          <strong>Notes:</strong> {transaction.notes || "N/A"}
        </p>
        {transaction.transactionItems?.length > 0 && (
          <>
            <h3>Items</h3>
            <table className="user-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Quantity</th>
                  <th>Unit Price</th>
                  <th>Discount</th>
                  <th>Line Total</th>
                </tr>
              </thead>
              <tbody>
                {transaction.transactionItems.map((item, index) => (
                  <tr key={index}>
                    <td>{item.product?.brandName || "N/A"}</td>
                    <td>{item.quantity}</td>
                    <td>₱{parseFloat(item.unitPrice).toFixed(2)}</td>
                    <td>₱{parseFloat(item.discount || 0).toFixed(2)}</td>
                    <td>₱{parseFloat(item.lineTotal).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
      <div className="modal-actions print-hide">
        {" "}
        {/* Added print-hide class */}
        {transaction.status !== "voided" && (
          <button
            type="button"
            className="button button-danger"
            onClick={() => onVoid(transaction.id)}
          >
            Void Transaction
          </button>
        )}
        {/* NEW BUTTON: Print to PDF */}
        <button
          type="button"
          className="button button-info" // Use a distinct button style, e.g., button-info
          onClick={handlePrint}
        >
          🖨️ Print as PDF
        </button>
        {/* END NEW BUTTON */}
        <button
          type="button"
          className="button button-secondary"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay">
      {mode === "form" &&
        (showConfirmation ? renderConfirmationContent() : renderFormContent())}
      {mode === "view" && renderViewContent()}
    </div>
  );
};

export default TransactionModal;
